'use client';
import { MouseEventHandler, ReactNode, useContext, useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { FormikHelpers, FormikErrors } from 'formik';

import './LoginForm.style.scss';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import SubmitButton from '@/components/form/lib/SubmitButton/SubmitButton';
import InputField from '@/components/form/lib/InputField/InputField';
import PasswordField from '@/components/form/lib/PasswordField/PasswordField';
import { UserContext } from '@/components/context/UserContext/UserContext';
import Link from '@/components/common/Link/Link';
import Text from '@/components/common/Text/Text';
import { SIGNUP_ROUTES } from '@/const/signup';
import { isValidEmail } from '@/util/regexp';
import { LoginBlockedError } from '@/util/auth';
import { User } from '@/types/User';

import EllaAiLogo from 'public/img/logo/ella-ai-tm.svg';
import { useFlags } from '@/components/hook/flags';
import { FLAG_KEYS } from '@/const/flags';

const LOGIN_FAILED_MESSAGE =
  'Your login details are incorrect. Forget your password? Reset it below';
// Shown when /api/login answers 403 browser_verification_failed (BotID).
const LOGIN_BLOCKED_MESSAGE =
  "We couldn't verify your browser. Please refresh the page and try again.";

export type LoginFormValues = {
  username: string;
  password: string;
};

export interface LoginFormProps
  extends Omit<
    BaseFormProps<LoginFormValues>,
    'children' | 'onSubmit' | 'onSubmitSuccess'
  > {
  headline?: ReactNode | string;
  redirectParam?: string;
  subHeadline?: string;
  onSubmit?: (
    values: LoginFormValues,
    actions: FormikHelpers<LoginFormValues>
  ) => void;
  onNavigateClick?: (e: MouseEventHandler<HTMLAnchorElement>) => void;
  onSubmitSuccess?: (values: LoginFormValues, user: User) => void;
}

function defaultValidation(
  values: LoginFormValues
): FormikErrors<LoginFormValues> {
  const { username, password } = values;
  const errors: FormikErrors<LoginFormValues> = {};
  if (!username) {
    errors.username = 'Username cannot be blank';
  }

  if (!isValidEmail(username)) {
    errors.username = 'Please enter a valid email format';
  }

  if (!password) {
    errors.password = 'Password field cannot be blank';
  }

  return errors;
}

export default function LoginForm(props: LoginFormProps) {
  const {
    headline = 'Login',
    subHeadline,
    initialValues,
    className = '',
    redirectParam,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    validateOnBlur = true,
    validateOnChange = false,
    onNavigateClick
  } = props;
  const searchParams = useSearchParams();
  const userContext = useContext(UserContext);
  const router = useRouter();
  const [formError, setFormError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { value: flagValues } = useFlags();

  const isEllaOn = flagValues[FLAG_KEYS.ELLA_CHAT];

  const isPasswordUpdated = searchParams.has('updated');
  let redirectRoute: string;

  if (!!redirectParam) {
    redirectRoute = `/${redirectParam}`;
  } else {
    redirectRoute = '/';
  }

  const handleSubmit = async (values: LoginFormValues) => {
    // Your form submission logic goes in this function.
    setFormError('');
    setLoading(true);
    try {
      const loginResponse = await userContext.actions.login(
        values.username,
        values.password
      );

      if (onSubmitSuccess) {
        onSubmitSuccess?.(values, loginResponse);
      } else {
        router.push(redirectRoute);
      }
    } catch (error) {
      console.error('LoginForm had an error');
      setFormError(
        error instanceof LoginBlockedError
          ? LOGIN_BLOCKED_MESSAGE
          : LOGIN_FAILED_MESSAGE
      );
      onSubmitFailure?.(values, error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form<LoginFormValues>
      className={cn('LoginForm', className)}
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      {redirectParam === 'ella' && isEllaOn ? (
        <>
          <Text
            className='LoginForm-title'
            variant='title-xl'
            italic
          >
            Login to use{' '}
            <Image
              className='LoginForm-title-ella-logo'
              alt='Ella ai'
              height={28}
              src={EllaAiLogo}
            />
          </Text>
        </>
      ) : (
        <Text
          className='LoginForm-title'
          variant='title-xl'
          italic
        >
          {headline}
        </Text>
      )}

      {subHeadline && (
        <Text
          className='LoginForm-subtitle'
          variant='text-s'
        >
          {subHeadline}
        </Text>
      )}

      {isPasswordUpdated && (
        <Text className='LoginForm-updated-password-text'>
          Password updated successfully!
        </Text>
      )}
      {formError && (
        <div className='LoginForm-formError-container'>
          <Text className='LoginForm-formError-text'>{formError}</Text>
        </div>
      )}
      <InputField
        className='email-field'
        name='username'
        label='Email Address'
      />
      <PasswordField
        name='password'
        label='Password'
      />
      <div className='LoginForm-forget-container'>
        <Text
          className='LoginForm-forget-text'
          variant='text-m'
        >
          Forgot password?&nbsp;
        </Text>
        <Link
          className='LoginForm-forget-link'
          variant='link-l'
          href='/login/forgot'
          underline
          compact
          onClick={onNavigateClick}
        >
          Reset password
        </Link>
      </div>
      <SubmitButton
        disabled={loading}
        isLoading={loading}
      >
        Submit
      </SubmitButton>
      <div className='LoginForm-apply-container'>
        <Text
          className='LoginForm-apply-text'
          variant='text-m'
        >
          Not a member?&nbsp;
        </Text>
        <Link
          className='LoginForm-apply-link'
          variant='link-l'
          href={SIGNUP_ROUTES.ACCOUNT}
          onClick={onNavigateClick}
          underline
          compact
        >
          Apply
        </Link>
      </div>
    </Form>
  );
}
